import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { listSeats, assignSeat, revokeSeat, reassignSeat, resendSeatInvite } from '../api';

// Accepts a pasted list: commas, semicolons, or one address per line (what a
// CSV column looks like when dropped into the box).
const parseEmails = (raw) => String(raw || '')
  .split(/[,;\s]+/)
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean)
  .filter((e, i, all) => all.indexOf(e) === i);

const ago = (value) => {
  if (!value) return null;
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 86400000);
  if (days <= 0) return 'today';
  return days === 1 ? 'yesterday' : `${days} days ago`;
};

function SeatRow({ seat, onChanged, readOnly = false }) {
  const [reassigning, setReassigning] = useState(false);
  const [email, setEmail] = useState('');

  const reassign = useMutation({
    mutationFn: () => reassignSeat(seat.id, { email: email.trim() }),
    onSuccess: () => { toast.success('Seat moved'); setReassigning(false); setEmail(''); onChanged(); },
    onError: (e) => toast.error(e?.message || 'Could not move the seat'),
  });
  const resend = useMutation({
    mutationFn: () => resendSeatInvite(seat.id),
    onSuccess: () => { toast.success('Invite sent again'); onChanged(); },
    onError: (e) => toast.error(e?.message || 'Could not resend the invite'),
  });
  const revoke = useMutation({
    mutationFn: () => revokeSeat(seat.id),
    onSuccess: onChanged,
    onError: (e) => toast.error(e?.message || 'Could not revoke the seat'),
  });

  const inv = seat.invitation;
  const holder = seat.user || seat.invited_email || 'open';
  const detail = seat.status === 'invited' && inv
    ? `invited${inv.last_sent_at ? ` ${ago(inv.last_sent_at)}` : ''}${inv.sent_count > 1 ? `, resent ${inv.sent_count - 1}x` : ''}${inv.expired ? ', link expired' : ''}`
    : seat.status;

  return (
    <div className="py-1.5 border-b border-gray-50 dark:border-gray-800 last:border-0">
      <div className="flex items-center justify-between gap-2 text-[12px]">
        <span className="min-w-0 truncate">
          {holder} <span className="text-gray-400">({detail})</span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          {!readOnly && seat.can_resend && (
            <button onClick={() => resend.mutate()} disabled={resend.isPending}
              className="text-blue-500 text-[11px] disabled:opacity-40">
              {resend.isPending ? 'sending...' : 'resend'}
            </button>
          )}
          {!readOnly && seat.status !== 'revoked' && (
            <button onClick={() => { setReassigning((v) => !v); setEmail(''); }} className="text-blue-500 text-[11px]">
              {reassigning ? 'cancel' : 'reassign'}
            </button>
          )}
          {!readOnly && seat.status !== 'revoked' && (
            <button onClick={() => revoke.mutate()} disabled={revoke.isPending} className="text-red-500 text-[11px] disabled:opacity-40">
              revoke
            </button>
          )}
        </span>
      </div>
      {reassigning && (
        <div className="flex gap-2 mt-1.5">
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && email.trim() && !reassign.isPending) reassign.mutate(); }}
            placeholder="move this seat to..."
            aria-label="New seat holder email"
            className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-transparent"
          />
          <button
            onClick={() => reassign.mutate()}
            disabled={!email.trim() || reassign.isPending}
            className="text-xs font-bold px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-40"
          >
            Move
          </button>
        </div>
      )}
      {reassigning && (
        <p className="text-[11px] text-gray-400 mt-1">
          The current holder loses access and any session they were registered for.
        </p>
      )}
    </div>
  );
}

export default function SeatManager({ purchaseId, readOnly = false }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const key = ['seats', purchaseId];
  const { data, isError } = useQuery({ queryKey: key, queryFn: () => listSeats(purchaseId) });
  const invalidate = () => qc.invalidateQueries({ queryKey: key });

  const emails = parseEmails(draft);
  const available = data?.available ?? 0;
  const overCapacity = emails.length > available;

  const invite = useMutation({
    mutationFn: () => assignSeat(purchaseId, { emails }),
    onSuccess: (res) => {
      const sent = res?.seats?.length || 0;
      const failed = res?.failed || [];
      if (sent > 0) toast.success(`${sent} invite${sent === 1 ? '' : 's'} sent`);
      failed.forEach((f) => toast.error(`${f.email}: ${f.error}`));
      setDraft('');
      invalidate();
    },
    onError: (e) => toast.error(e?.message || 'Could not send the invites'),
  });

  if (isError) return <div className="mt-3 text-xs text-red-500">Could not load seats. Please refresh.</div>;

  return (
    <div className="mt-3 border-t border-gray-100 dark:border-gray-800 pt-3">
      <div className="text-xs text-gray-500 mb-2">
        Seats: {data?.available ?? '-'} of {data?.total ?? '-'} available
      </div>
      {(data?.seats || []).map((s) => (
        <SeatRow key={s.id} seat={s} onChanged={invalidate} readOnly={readOnly} />
      ))}

      {!readOnly && (
        <div className="mt-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={emails.length > 1 ? 3 : 1}
            placeholder="invite by email (paste a list to invite several at once)"
            aria-label="Invite seat holders by email"
            className="w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-transparent resize-y"
          />
          <div className="flex items-center justify-between gap-2 mt-1.5">
            <span className="text-[11px] text-gray-400">
              {emails.length > 0
                ? `${emails.length} address${emails.length === 1 ? '' : 'es'}${overCapacity ? ` - only ${available} seat${available === 1 ? '' : 's'} left` : ''}`
                : 'Commas, semicolons, or one per line.'}
            </span>
            <button
              disabled={emails.length === 0 || available <= 0 || overCapacity || invite.isPending}
              onClick={() => invite.mutate()}
              className="text-sm font-bold px-3 py-2 rounded-lg bg-orange-500 text-white disabled:opacity-40"
            >
              {invite.isPending ? 'Sending...' : `Invite${emails.length > 1 ? ` ${emails.length}` : ''}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
